import React, { useEffect, useMemo, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import Dialog from '@mui/material/Dialog';
import Search from 'lucide-react/dist/esm/icons/search';
import Globe from 'lucide-react/dist/esm/icons/globe';
import X from 'lucide-react/dist/esm/icons/x';
import Check from 'lucide-react/dist/esm/icons/check';

import TravelKitCountryFlag from './TravelKitCountryFlag';
import {
  DESTINATIONS,
  QUICK_PICK_CODES,
  destinationName,
  findDestination,
  matchesDestination,
  travelKitLangOfLocale
} from './destinations';

const REGION_ORDER = ['asia', 'europe', 'americas', 'oceania', 'africa'];

const REGION_LABELS = {
  asia: { id: 'travelkit-region-asia', defaultMessage: '亞洲' },
  europe: { id: 'travelkit-region-europe', defaultMessage: '歐洲' },
  americas: { id: 'travelkit-region-americas', defaultMessage: '美洲' },
  oceania: { id: 'travelkit-region-oceania', defaultMessage: '大洋洲' },
  africa: { id: 'travelkit-region-africa', defaultMessage: '非洲' }
};

/**
 * The destination picker, modelled on the one the traveller sees
 * (`travelkit/src/components/CountrySearchModal.tsx`): search over every
 * language plus the ISO and calling codes, the same five quick picks, then the
 * full list grouped by region.
 *
 * A `<Select>` was fine for fourteen hardcoded countries. It is the wrong
 * control for the full set the SOS page can render, and it also cannot say
 * which destinations exist — an adviser who scrolls past "Switzerland" in a
 * dropdown learns nothing; one who types "swiss" and gets a hit does.
 */
const TravelKitCountryPickerDialog = ({ intl, open, value, onSelect, onClose }) => {
  const travelKitLang = travelKitLangOfLocale(intl.locale);
  const [query, setQuery] = useState('');
  const searchRef = useRef(null);

  // Each opening starts from the whole list. Reopening onto someone else's
  // half-typed search is the kind of state that reads as a bug.
  useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  const quickPicks = useMemo(() => QUICK_PICK_CODES.map(findDestination).filter(Boolean), []);

  const grouped = useMemo(() => {
    const matches = DESTINATIONS.filter(d => matchesDestination(d, query));
    return REGION_ORDER.map(region => ({
      region,
      items: matches.filter(d => d.region === region)
    })).filter(group => group.items.length > 0);
  }, [query]);

  const matchCount = grouped.reduce((sum, group) => sum + group.items.length, 0);

  const handlePick = code => {
    onSelect(code);
    onClose();
  };

  const renderRow = destination => {
    const selected = destination.code === value;
    return (
      <button
        key={destination.code}
        type="button"
        role="option"
        aria-selected={selected}
        className={`travelkit-country-row ${selected ? 'is-selected' : ''}`}
        onClick={() => handlePick(destination.code)}
      >
        <TravelKitCountryFlag code={destination.code} flag={destination.flag} size={26} />
        <span className="travelkit-country-row-name">{destinationName(destination, travelKitLang)}</span>
        <span className="travelkit-country-row-dial">{destination.callingCode}</span>
        {selected && <Check size={16} className="travelkit-country-row-tick" />}
      </button>
    );
  };

  return (
    <Dialog
      className="travelkit-country-picker-dialog"
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      // v9 moved both of these onto `slotProps`; the old top-level `PaperProps`
      // / `TransitionProps` still compile and silently do nothing, which is what
      // `npm run scan:mui` exists to catch.
      slotProps={{
        paper: { style: { borderRadius: 18, overflow: 'hidden' } },
        transition: { onEntered: () => searchRef.current && searchRef.current.focus() }
      }}
    >
      <div className="travelkit-picker-head">
        <span className="travelkit-picker-head-icon">
          <Globe size={18} />
        </span>
        <h4>{intl.formatMessage({ id: 'travelkit-country-label', defaultMessage: '目的地' })}</h4>
        <button
          type="button"
          className="travelkit-picker-close"
          onClick={onClose}
          aria-label={intl.formatMessage({ id: 'Close', defaultMessage: '關閉' })}
        >
          <X size={16} />
        </button>
      </div>

      <div className="travelkit-picker-search">
        <div className="travelkit-picker-search-field">
          <Search size={15} className="travelkit-picker-search-icon" />
          <input
            ref={searchRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'travelkit-country-search-placeholder',
              defaultMessage: '搜尋國家 / 地區、國際區號'
            })}
          />
          {!!query && (
            <button type="button" className="travelkit-picker-search-clear" onClick={() => setQuery('')}>
              {intl.formatMessage({ id: 'Clear', defaultMessage: '清除' })}
            </button>
          )}
        </div>

        <div className="travelkit-picker-quick">
          {quickPicks.map(destination => (
            <button
              key={destination.code}
              type="button"
              className={`travelkit-quick-chip ${destination.code === value ? 'is-selected' : ''}`}
              onClick={() => handlePick(destination.code)}
            >
              <TravelKitCountryFlag code={destination.code} flag={destination.flag} size={18} />
              <span>{destinationName(destination, travelKitLang)}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="travelkit-picker-list" role="listbox">
        {matchCount === 0 && (
          <p className="travelkit-picker-empty">
            {intl.formatMessage({
              id: 'travelkit-country-no-match',
              defaultMessage: '未有相符的目的地。旅行小錦囊只涵蓋已預載當地緊急熱線的國家 / 地區。'
            })}
          </p>
        )}
        {grouped.map(group => (
          <div className="travelkit-picker-group" key={group.region}>
            <div className="travelkit-picker-group-title">{intl.formatMessage(REGION_LABELS[group.region])}</div>
            {group.items.map(renderRow)}
          </div>
        ))}
      </div>
    </Dialog>
  );
};

export default injectIntl(TravelKitCountryPickerDialog);
